import React from 'react';
import styled from 'styled-components';
import { Target, Info, Weight, ChevronRight } from 'lucide-react';
import { theme } from '../styles/theme';
import { EvaluationCriteria } from '../types';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
`;

const CriteriaCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all ${theme.transitions.base};
  position: relative;

  &:hover {
    border-color: ${theme.colors.accent[400]};
    box-shadow: 0 4px 12px ${theme.colors.accent[500]}20;
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, ${theme.colors.accent[500]}, ${theme.colors.accent[600]});
  }
`;

const CardHeader = styled.div`
  padding: ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.accent[50]}, ${theme.colors.accent[100]}20);
`;

const CriteriaTitle = styled.h4`
  margin: 0 0 ${theme.spacing[2]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${theme.colors.accent[500]};
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const WeightBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.success[100]};
  color: ${theme.colors.success[700]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-left: auto;
`;

const CardBody = styled.div`
  padding: ${theme.spacing[5]};
`;

const Description = styled.p`
  margin: 0 0 ${theme.spacing[4]} 0;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ScoringMethod = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
`;

const MethodIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.primary[600]};
`;

const MethodLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  margin-right: ${theme.spacing[2]};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.primary[500]};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
`;

interface EvaluationCriteriaGridProps {
  criteria: EvaluationCriteria[];
}

const EvaluationCriteriaGrid: React.FC<EvaluationCriteriaGridProps> = ({ criteria }) => {
  if (!criteria || criteria.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>
          <Target size={24} />
        </EmptyIcon>
        <EmptyText>No evaluation criteria found yet...</EmptyText>
      </EmptyState>
    );
  }

  return (
    <GridContainer>
      {criteria.map((criterion, index) => (
        <CriteriaCard key={criterion.id || index}>
          <CardHeader>
            <CriteriaTitle>
              <TitleIcon>
                <Target size={16} />
              </TitleIcon>
              {criterion.name}
              <WeightBadge>
                <Weight size={14} />
                {criterion.weight}%
              </WeightBadge>
            </CriteriaTitle>
          </CardHeader>
          
          <CardBody>
            <Description>{criterion.description}</Description>
            
            {criterion.scoring_method && (
              <ScoringMethod>
                <MethodIcon>
                  <Info size={14} />
                </MethodIcon>
                <div>
                  <MethodLabel>Scoring Method:</MethodLabel>
                  {criterion.scoring_method}
                </div>
                <ChevronRight size={16} style={{ marginLeft: 'auto', color: theme.colors.textMuted }} />
              </ScoringMethod>
            )}
          </CardBody>
        </CriteriaCard>
      ))}
    </GridContainer>
  );
};

export default EvaluationCriteriaGrid;